import { I18nProvider } from '@vlm/i18n';
import { Toaster, UiProvider } from '@vlm/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import type { Role } from '@vlm/api';
import { messages, useT } from './i18n';
import { ADMINS, MANAGERS, restoreSession } from './lib/auth';
import { usePrefs } from './lib/prefs';
import { queryClient } from './lib/query';
import { Layout, RequireRole } from './shell/Layout';

const Login = lazy(() => import('./pages/Login'));
const LiveMap = lazy(() => import('./pages/LiveMap'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const VehicleForm = lazy(() => import('./pages/VehicleForm'));
const Devices = lazy(() => import('./pages/Devices'));
const DeviceForm = lazy(() => import('./pages/DeviceForm'));
const Assignments = lazy(() => import('./pages/Assignments'));
const LinkDevice = lazy(() => import('./pages/LinkDevice'));
const Trips = lazy(() => import('./pages/Trips'));
const TripDetail = lazy(() => import('./pages/TripDetail'));
const Geofences = lazy(() => import('./pages/Geofences'));
const GeofenceEditor = lazy(() => import('./pages/GeofenceEditor'));
const Alerts = lazy(() => import('./pages/Alerts'));
const AlertSettings = lazy(() => import('./pages/AlertSettings'));
const Users = lazy(() => import('./pages/Users'));
const UserForm = lazy(() => import('./pages/UserForm'));
const Profile = lazy(() => import('./pages/Profile'));
const Billing = lazy(() => import('./pages/Billing'));
const BillingDetails = lazy(() => import('./pages/BillingDetails'));
const Reports = lazy(() => import('./pages/reports/Reports'));
const TripReport = lazy(() => import('./pages/reports/TripReport'));
const ActivityReport = lazy(() => import('./pages/reports/ActivityReport'));
const TrackExplorer = lazy(() => import('./pages/reports/TrackExplorer'));
const AlertReport = lazy(() => import('./pages/reports/AlertReport'));
const GeofenceReport = lazy(() => import('./pages/reports/GeofenceReport'));
const NotFound = lazy(() => import('./pages/NotFound'));

const only = (roles: readonly Role[], Page: ComponentType) => (
  <RequireRole roles={roles}>
    <Page />
  </RequireRole>
);

/**
 * The old admin's routes, kept where they were; the old /dashboard and /reports/* addresses still land.
 * Every form is a page of its own (operator, 2026-10-24: no popup forms) — the …/new and …/edit routes.
 */
const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={null}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/live" replace /> },
      { path: 'dashboard', element: <Navigate to="/live" replace /> },
      { path: 'live', element: <LiveMap /> },
      { path: 'vehicles', element: only(MANAGERS, Vehicles) },
      { path: 'vehicles/new', element: only(MANAGERS, VehicleForm) },
      { path: 'vehicles/:vehicleId/edit', element: only(MANAGERS, VehicleForm) },
      { path: 'devices', element: only(MANAGERS, Devices) },
      { path: 'devices/new', element: only(MANAGERS, DeviceForm) },
      { path: 'devices/:deviceId/edit', element: only(MANAGERS, DeviceForm) },
      { path: 'assignments', element: only(MANAGERS, Assignments) },
      { path: 'assignments/new', element: only(MANAGERS, LinkDevice) },
      { path: 'trips', element: <Trips /> },
      { path: 'trips/:tripId', element: <TripDetail /> },
      { path: 'geofences', element: only(MANAGERS, Geofences) },
      { path: 'geofences/new', element: only(MANAGERS, GeofenceEditor) },
      { path: 'geofences/:geofenceId/edit', element: only(MANAGERS, GeofenceEditor) },
      { path: 'alerts', element: <Alerts /> },
      { path: 'settings/alerts', element: only(ADMINS, AlertSettings) },
      { path: 'users', element: only(ADMINS, Users) },
      { path: 'users/new', element: only(ADMINS, UserForm) },
      { path: 'users/:userId/edit', element: only(ADMINS, UserForm) },
      { path: 'billing', element: only(ADMINS, Billing) },
      { path: 'billing/details', element: only(ADMINS, BillingDetails) },
      { path: 'profile', element: <Profile /> },
      { path: 'reports', element: <Reports /> },
      { path: 'reports/trips', element: <TripReport /> },
      { path: 'reports/activity', element: <ActivityReport /> },
      { path: 'reports/track', element: <TrackExplorer /> },
      { path: 'reports/vehicle-activity', element: <Navigate to="/reports/track" replace /> },
      { path: 'reports/trips/map', element: <Navigate to="/reports/track" replace /> },
      { path: 'reports/alerts', element: <AlertReport /> },
      { path: 'reports/geofences', element: <GeofenceReport /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

function Ui({ children }: { children: ReactNode }) {
  const { t, dir } = useT();
  return (
    <UiProvider dir={dir}>
      {children}
      <Toaster dismissLabel={t('common.dismiss')} className="top-[64px]" />
    </UiProvider>
  );
}

export function App() {
  const lang = usePrefs((s) => s.lang);
  useEffect(() => {
    void restoreSession();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider lang={lang} messages={messages}>
        <Ui>
          <RouterProvider router={router} />
        </Ui>
      </I18nProvider>
    </QueryClientProvider>
  );
}
